import { createSelector } from 'reselect'
import { getCruiseItems, hasLuxPlusInclusionsInCruiseBooking, getCurrentCheckoutStepId as getCruiseCheckoutStepId } from './cruiseSelectors'
import { getTourCheckoutSteps } from 'components/App/Header/HeaderCheckout/StepsHeaderCheckout/steps/tours'
import { CRUISE_CHECKOUT_HEADER_STEPS } from 'components/App/Header/HeaderCheckout/StepsHeaderCheckout/steps/cruises'
import { buildHotelCheckoutSteps } from 'components/App/Header/HeaderCheckout/StepsHeaderCheckout/steps/hotels'
import { splitMobileAppLikeCheckoutPurchaseStep } from 'components/App/Header/HeaderCheckout/StepsHeaderCheckout/steps/mobileAppLikeCheckout'
import { getCheckoutRouteFromURL } from 'checkout/lib/utils/misc/steps'
import { EmptyArray } from 'lib/array/arrayUtils'
import { CheckoutPageId } from 'checkout/constants/pages'
import { Experiments } from 'constants/experiments'
import { getCanRedeemLuxPlusBenefits, getShouldShowFlightsBeforeLuxPlusUpsell, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { FLIGHTS_CHECKOUT_HEADER_STEPS, FlightChangeDatesPageStep } from 'components/App/Header/HeaderCheckout/StepsHeaderCheckout/steps/flights'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import { getFlightChangeDatesPageLocation } from 'checkout/lib/utils/flights/dateChangeUtils'
import { hasCruiseHotelRecommendationsAvailable } from 'checkout/selectors/cruiseSelectors'
import {
  getExperimentEnabledDecision,
  getOptimizelyExperimentVariation,
} from 'lib/optimizely/optimizelyUtils'
import { hasLuxPlusCheckoutItems } from './view/luxPlusSubscription'
import { getIsCharterFlightCheckout, getNonCharterFlightItems } from './view/flights'
import { getAccommodationItems } from './view/accommodation'
import getExperienceItems from './view/items/getExperienceItems'
import getTransferItems from './view/items/getTransferItems'
import { getTourV1Items } from './view/tourv1'
import { getTourV2Items } from './view/toursv2'
import {
  getShouldShowLuxPlusBeforeCharterFeederFlights,
  hasInsuranceItem,
  isAddFlightsStepAvailable,
  isAirportTransfersStepAvailable,
  isExperiencesSupportedToAddToCart,
  isLuxPlusStandaloneStepAvailable,
} from './navigationSelectors'
import { getIsMobileAppLikeCheckoutEnabled } from './mobileAppLikeCheckoutSelectors'

const getHotelCheckoutStepperEligibleSteps = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (state: App.State) => hasLuxPlusCheckoutItems(state),
  (state: App.State) => getNonCharterFlightItems(state),
  (state: App.State) => getTransferItems(state, false),
  (state: App.State) => getExperienceItems(state),
  (state: App.State) => getTourV1Items(state),
  (state: App.State) => getTourV2Items(state),
  (state: App.State) => isLuxPlusStandaloneStepAvailable(state),
  (state: App.State) => isAirportTransfersStepAvailable(state),
  (state: App.State) => isExperiencesSupportedToAddToCart(state),
  (state: App.State) => getCheckoutRouteFromURL(state.router.location.pathname),
  (state: App.State) => getIsCharterFlightCheckout(state),
  (state: App.State) => getShouldShowFlightsBeforeLuxPlusUpsell(state),
  (state: App.State) => getShouldShowLuxPlusBeforeCharterFeederFlights(state),
  (state: App.State) => isAddFlightsStepAvailable(state),
  (
    accommodationItems,
    luxPlusAlreadyInCart,
    flightItems,
    transferItems,
    experienceItems,
    tourV1Items,
    tourV2Items,
    isLuxPlusStepAvailable,
    isAirportTransfersAvailable,
    isExperiencesStepAvailable,
    currentRoute,
    isCharterCheckout,
    shouldShowFlightsBeforeLuxPlusUpsell,
    shouldShowLuxPlusBeforeCharterFeeder,
    isAddFlightsAvailable,
  ): Array<App.Checkout.CheckoutStep> => {
    if (tourV1Items.length > 0 || tourV2Items.length > 0) {
      return EmptyArray
    }

    const steps = buildHotelCheckoutSteps({
      hasAccommodationItems: accommodationItems.length > 0,
      currentRoute,
      isLuxPlusStepAvailable,
      isFlightsStepAvailable: isAddFlightsAvailable && !isCharterCheckout,
      isAirportTransfersStepAvailable: isAirportTransfersAvailable,
      isExperiencesStepAvailable,
      hasLuxPlusCheckoutItems: luxPlusAlreadyInCart,
      hasFlightItems: flightItems.length > 0,
      hasTransferItems: transferItems.length > 0,
      hasExperienceItems: experienceItems.length > 0,
      shouldShowFlightsBeforeLuxPlusUpsell: shouldShowFlightsBeforeLuxPlusUpsell && !shouldShowLuxPlusBeforeCharterFeeder,
    })

    return steps.length ? steps : EmptyArray
  },
)

const getHotelCheckoutSteps = createSelector(
  (state: App.State) => getExperimentEnabledDecision(
    getOptimizelyExperimentVariation(state, Experiments.croCheckoutProgressStepper.id),
  ),
  (state: App.State) => getHotelCheckoutStepperEligibleSteps(state),
  (isCheckoutProgressStepperEnabled, hotelCheckoutStepperEligibleSteps): Array<App.Checkout.CheckoutStep> => {
    return isCheckoutProgressStepperEnabled ? hotelCheckoutStepperEligibleSteps : EmptyArray
  },
)

export const selectIsHotelCheckoutProgressStepperExposure = createSelector(
  (state: App.State) => getTourCheckoutSteps(state),
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getHotelCheckoutStepperEligibleSteps(state),
  (
    tourCheckoutSteps,
    cruiseItems,
    postPurchaseMode,
    hotelCheckoutStepperEligibleSteps,
  ): boolean => {
    if (cruiseItems.length > 0) {
      return false
    }

    if (tourCheckoutSteps.length > 1) {
      return false
    }

    if (postPurchaseMode) {
      return false
    }

    return hotelCheckoutStepperEligibleSteps.length > 0
  },
)

export const selectCheckoutSteps = createSelector(
  (state: App.State) => getTourCheckoutSteps(state),
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => getIsLuxPlusEnabled(state),
  (state: App.State) => getCanRedeemLuxPlusBenefits(state),
  (state: App.State) => hasLuxPlusInclusionsInCruiseBooking(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => hasCruiseHotelRecommendationsAvailable(state),
  (state: App.State) => hasInsuranceItem(state),
  (state: App.State) => getHotelCheckoutSteps(state),
  (state: App.State) => getIsMobileAppLikeCheckoutEnabled(state),
  (
    tourCheckoutSteps,
    cruiseItems,
    luxPlusEnabled,
    checkCanRedeemLuxPlusBenefits,
    hasLuxPlusInclusionsInCruiseBooking,
    postPurchaseMode,
    hasCruiseHotelRecommendationsAvailable,
    orderHasInsurance,
    hotelCheckoutSteps,
    isMobileAppLikeCheckoutEnabled,
  ): Array<App.Checkout.CheckoutStep> => {
    const isCruise = cruiseItems.length > 0
    let allCruiseSteps = CRUISE_CHECKOUT_HEADER_STEPS

    if (postPurchaseMode === 'flight-change-dates') {
      // Filter out insurance step if order doesn't have insurance
      if (!orderHasInsurance) {
        return FLIGHTS_CHECKOUT_HEADER_STEPS.filter(
          step => step.key !== FlightChangeDatesPageStep.INSURANCE_SELECT_DATES,
        )
      }
      return FLIGHTS_CHECKOUT_HEADER_STEPS
    }

    if (isCruise) {
      // If LuxPlus is not enabled, remove it from the steps
      if (!luxPlusEnabled || !hasLuxPlusInclusionsInCruiseBooking || checkCanRedeemLuxPlusBenefits) {
        allCruiseSteps = allCruiseSteps.filter(step => step.key !== CheckoutPageId.LuxPlus)
      }

      // If hotel recommendations A/B test is not enabled, remove it from the steps
      if (!hasCruiseHotelRecommendationsAvailable) {
        allCruiseSteps = allCruiseSteps.filter(step => step.key !== CheckoutPageId.CruiseHotelRecommendation)
      }

      return splitMobileAppLikeCheckoutPurchaseStep(
        allCruiseSteps,
        isMobileAppLikeCheckoutEnabled,
      )
    }

    if (tourCheckoutSteps.length > 1) {
      return tourCheckoutSteps
    }

    if (postPurchaseMode) {
      return EmptyArray
    }

    return splitMobileAppLikeCheckoutPurchaseStep(
      hotelCheckoutSteps,
      isMobileAppLikeCheckoutEnabled,
    )
  },
)

export const selectCheckoutCurrentStep = createSelector(
  (state: App.State) => state.router.location.pathname,
  (state: App.State) => getCruiseCheckoutStepId(state),
  (state: App.State) => getTourCheckoutSteps(state),
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => selectCheckoutSteps(state),
  (state: App.State) => state.flights.flightsDateChange.currentStep,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (
    pathname,
    cruiseCheckoutStepId,
    tourCheckoutSteps,
    cruiseItems,
    steps,
    flightsDateChangeStep,
    postPurchaseMode,
  ): App.Checkout.CheckoutStep | undefined => {
    const isCruise = cruiseItems.length > 0

    let stepKey: string | undefined
    if (isCruise) {
      stepKey = cruiseCheckoutStepId
    }

    if (tourCheckoutSteps.length > 1) {
      stepKey = getCheckoutRouteFromURL(pathname)
    }

    if (postPurchaseMode === 'flight-change-dates') {
      // Use URL detection to determine current step for flight change dates flow
      // This handles cases where Redux state may not match the actual page (e.g., on purchase page)
      const { isOnPurchasePage, isOnInsurancePage } = getFlightChangeDatesPageLocation(pathname)
      if (isOnPurchasePage) {
        return FLIGHTS_CHECKOUT_HEADER_STEPS.find(step => step.key === FlightChangeDatesPageStep.REVIEW_AND_PAY)
      }
      if (isOnInsurancePage) {
        return FLIGHTS_CHECKOUT_HEADER_STEPS.find(step => step.key === FlightChangeDatesPageStep.INSURANCE_SELECT_DATES)
      }
      // For internal steps within flight-change-select-flight-to-change page, use Redux state
      return FLIGHTS_CHECKOUT_HEADER_STEPS.find(step => step.key === flightsDateChangeStep)
    }

    if (!stepKey && steps.length) {
      stepKey = getCheckoutRouteFromURL(pathname)
    }

    return steps.find(step => {
      return step.key === stepKey ||
        // if any sub-step matches the current key, we pick the parent as the current active step.
        step.subSteps?.some(subStep => subStep.key === stepKey)
    })
  },
)
